import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from "react-redux";
import { Box, FormControlLabel, Grid, IconButton, Stack, Switch, Tooltip, Typography } from "@mui/material";
import GroupsIcon from '@mui/icons-material/Groups';
import UploadIcon from '@mui/icons-material/Upload';
import {
    Portlet, PortletBody,
    PortletHeader, PortletHeaderToolbar
} from "../../partials/content/Portlet";


import { routes } from '../../router/Routes';

import makeStyles from '@mui/styles/makeStyles';
import { BreadCrumb } from '../../common/Breadcrumb';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { TableLevelAction, useDefaultFiltersState, useTableFilter, whatChanged } from '../../common';
import { AcknowledgeDialog } from './AcknowledgeDialog';
import { FlagDialog } from './FlagDialog';

import { UpdateOutlined } from '@mui/icons-material';
import FlagIcon from '@mui/icons-material/Flag';
import GetAppIcon from '@mui/icons-material/GetApp';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { useData, useStatePtr } from '@pure-ptr/react';
import { useFitHeight } from '../../../_metronic/layout/Layout';
import { useGlobalStore } from '../../common';
import SelectSearchTitle from '../../common/searchTitle/LocalStateSearchTitle';
import { fetchSearchCriteriaForPage } from '../../store/api/search';
import { SEARCH_PERIOD_ID } from '../../store/constants/settings';
import { fetchClaimAnalysisGridData, fetchClaimAnalysisLanding, fetchClaimAnalysisTotals } from '../../store/sagas/pages';
import { ClaimAnalysisDataGrid } from './ClaimAnalysisGrid';
import { ClaimAnalysisTopFilters } from './ClaimAnalysisTopFilters';
import { ClaimStatistics } from './ClaimStatistics';
import { ProcessClaimDialog } from './ProcessDialog';
import { IgnoreFiltersDialog } from './ServerSideSearchAndIgnoreFiltersAcknowledgeDialog';
import UpdateStatusDialog from './UpdateStatusDialog';
import { toLocalStorage } from '../../util/storage';
import ClaimAssignmentDialog  from './ClaimAssignmentDialog/ClaimAssignmentDialog';
import { getAssignedFilter } from '../../util/pagination';
import { defaultTableFilterState } from '../../common';
import { selectHasRcmTeamAccess, selectIsSuperAdmin, selectIsUniversalAdmin,selectHasPatternIntelligenceAccess} from '../../store/selectors/session';
import RCMTeamAssignmentDialog from './RCMTeamAssignmentDialog/RCMTeamAssignmentDialog';
import { getAllRCMTeamsList } from '../../store/api/rcmAssignment';
import { importRcmAssignmentFile } from '../../store/api/claimAnalysis';
import { errorToastify } from '../../util/toast';
import { ToastMessage } from "../../common";
import { allClaimAnalysisGridColumns } from '../../store/constants/pages';
import { fromLocalStorage } from '../../util/storage';
import ExportPopover from './ExportPopover/ExportPopover';
import { getValidUsers } from '../../store/api/claimAssignment';
import { AIChatSection } from './AIChatSection';
import { RootState } from '../../store/reducers';
import { getEAPGCreditUsage } from '../../store/api/eapg';
import { ContractInfo } from '../../store/api/ai-api';
import { ClaimAnalysisFollowUpFilter } from './ClaimAnalysisFollowUpFilter';
import { infoToastify } from "../../util/toast";
import { CopyClaimDialog } from './CopyClaimDialog';
import CopyIcon from '@mui/icons-material/FileCopyOutlined';

const useStyles = makeStyles(theme => ({
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(1),
        zIndex: 100,
    },
    formControl: {
        margin: theme.spacing(1),
        width: 200,
    },
    formControlClaim: {
        margin: theme.spacing(1),
        width: 71
    },
    formControlStatus: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: 4,
        marginRight: 4,
        width: 140,
        '@media (max-width: 1440px)': {
            width: 110,
        }
    },
    popover: {
        pointerEvents: 'none',
    },
    paper: {
        pointerEvents: 'auto',
        width: 300,
    },
    switch: {
        marginRight: 0,
        marginTop: 20,
        marginLeft: 0
    },
    gridMDMargin: {
        '@media (max-width: 1023px)': {
            paddingLeft: 10,
        }
    },
    IconButton: {
        padding: 6
    },
    statisticsContainer:{
        padding:'20px',
        paddingBottom:0
    }
}));

const traceChanges = whatChanged();


export const generateErrorToaster = (error,placeholderErrorMsg=null) => {
    const defaultErrorMsg = "Something Went Wrong, Please Try Again Later"
    errorToastify(
        <ToastMessage
            errorMessage={error?.payload?.error || placeholderErrorMsg || defaultErrorMsg}
            innerException={error?.payload?.innerException || placeholderErrorMsg || defaultErrorMsg}
            errorDetails={error?.payload?.errorDetails || placeholderErrorMsg || defaultErrorMsg}
        />
    );

}

export default function ClaimAnalysisPage({
    intl,
    claimStatusList,
    hasClaimProcessorAccess, 
    defaultCustomFilters, 
    selectedClientName,
    history,
    actions: {
        claimAnalysisSetDrilldown,
        downloadAnalysisReport,
        flagClaims,
        getTinyContracts,
        getPayersTiny,
        getClaimStatuses,
        getNpiRoles,
        resetClaimProcessError,
        setFlagToggle,
        setBulkFlagged,
        setOpenPlayDialog,
        setBulkAcknowledge,
        getFacilities,
        getUsersList,
        getPayerContractsTiny,
        getCodeServicesTiny,
        getDischargeStatusCodes,
        setMulti
    }
} ) {
    useFitHeight();

    const [totals, setTotals] = useState<any>();
    const isDevEnv = window.location.host === "dev.slicedhealth.com" || window.location.host === "localhost";

    // Legacy redux store selectors
    const queryParams = new URLSearchParams(window.location.search);
    const preferredAssignee=queryParams.get('assignedTo')
    const [aiModeEnabled, setAiModeEnabled] = useState(false);
    const [aiCustomFilter, setAiCustomFilter] = useState('');
    const [aiMessageToSend, setAiMessageToSend] = useState('');
    const [refetchCredits, setRefetchCredits] = useState(0);
    const [aiFiltersNL, setAiFiltersNL] = useState<string>('');
    const [isProcessingAIQuery, setIsProcessingAIQuery] = useState(false);
    const [isChatExpanded, setIsChatExpanded] = useState(false);
    
    // Store filters that were applied before AI mode was enabled
    // These will be sent with every AI query
    const [preAiModeFilters, setPreAiModeFilters] = useState<any>(null);
    
    // State to trigger filters-to-nl API call
    // Counter is used to force new reference even when filter content is the same
    const [filtersToConvert, setFiltersToConvert] = useState<{ filter: any; sort: any[]; counter?: number; significant_variance?: boolean; confidence_score?: boolean } | null>(null);
    const filtersToConvertCounterRef = React.useRef(0);
    
    // Store the complete AI filter structure (bypasses getPaginationFilter)
    const [aiAppliedFilters, setAiAppliedFilters] = useState<any>(null);
    
    
    // AI Access check - fetch credits and validate
    const hasClaimSemanticSearchAccess = useSelector<RootState>((state) => state.clients.hasClaimSemanticSearchAccess);
    const clientId = useSelector<RootState>((state) => state.dashboards.selectedClient);
    const role = useSelector<RootState>((state) => state.session.user?.role || null );

    
    // Fetch credit usage
    const { data: credits, isPending: isFetchingCredits } = useData(async () => {
        if (!role) return null;
        const credits = await getEAPGCreditUsage("ai");
        return credits;
    }, [role, refetchCredits]);
    
    // Check if user can use AI based on credits
    const chatBufferValue = 5; // Buffer to prevent hitting the limit exactly
    
    const haveAIAccess = useMemo(() => {
        const aiCredits = credits || {};        
        const hasCredits = Boolean(
            aiCredits?.totalLimit &&
            aiCredits?.usedCredits + chatBufferValue < aiCredits?.totalLimit
        );
        
        return Boolean(hasCredits);
    }, [credits]);

    const {
        data:rcmAssignmentData,
    } = useData(async () => {
        const allRcmTeams = await getAllRCMTeamsList();
        return allRcmTeams;
    }, []);
        

    const {
        data: userData,
    } = useData(async () => {
        const validUsers = await getValidUsers();
        return validUsers;
    }, []);

    // Fetch natural language description of filters
    const { data: filtersNLResponse } = useData(async () => {
        if (!filtersToConvert) return null;
        
        const contractInfo = new ContractInfo();
        const response = await contractInfo.filtersToNaturalLanguage(
            filtersToConvert.filter, 
            filtersToConvert.sort,
            filtersToConvert.significant_variance,
            filtersToConvert.confidence_score
        );
        
        if (response?.filters_nl) {
            setAiFiltersNL(`Applied filters: ${response.filters_nl}`);
        } else {
            setAiFiltersNL('');
        }
        
        return response;
    }, [filtersToConvert]);


    const isSummary= window.location.href.includes("fromSummary")
    const totalParams=history.location?.search?.split("&")?.length
    const user=useSelector((state: any) => state?.session?.user

    
)
    const canAccessRCM=useSelector(selectHasRcmTeamAccess)
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    const isUniversalAdmin = useSelector(selectIsUniversalAdmin);
    const hasPatternIntelligenceAccess = useSelector(selectHasPatternIntelligenceAccess);


    const claimAnalysisDownloadReportPending = useGlobalStore( x => x.claimAnalysis.download.pending );

    const relativeFilter = useGlobalStore( x => 
        x.settings.data.find( setting => 
            setting.id === SEARCH_PERIOD_ID
        )
        ?.value ?? '' 
    );

    const classes = useStyles();

    const [importFile, setImportFile] = useState(null);
    const importInputRef = useRef(null);
    const isClaimActionAppliedEnabled = (filters) => {
        const claimActionFilter =
            filters.filter?.find((item) => item?.field === "isClaimActionApplied");

        const isClaimActionApplied =
            (claimActionFilter?.equator === "equals" &&
                (claimActionFilter?.value === true ||
                    claimActionFilter?.value === "true")) ||
            (claimActionFilter?.equator === "not_equal_to" &&
                (claimActionFilter?.value === false ||
                    claimActionFilter?.value === "false"));

        const paidFilter = filters.customFilter?.find(
            (item) => item?.field === "isPaid" && item?.value == true,
        );

        let denialFilter = filters.customFilter?.find(
            (item) => item?.field === "isDenial" && item?.value == true,
        );
        if(!denialFilter){
            denialFilter = filters.filter?.find(
                (item) =>
                    item?.field === "isDenial" &&
                    (item?.value == true || item?.value == 'true'),
            );
        }

        return isClaimActionApplied && (!paidFilter || !denialFilter);
    };

    function handleImportFileInput() {
        importInputRef.current?.click();
    }

    function handleFileChange(e) {
        const file = e.target.files[0];

        if (file && file.type === "text/csv") {
            setImportFile(file);
            e.target.value = null; // reset input
        } else if (file) {
            errorToastify(
                "Invalid format, please make sure the file is in CSV format."
            );
        }
    }

    const notIsSwitchingClientAsPerUserRequest = () => {
        // - Check if URL contains 'assignedTo' and 'Preferred Client' (Email Template Call) to detect client switch.
        // - Stop operations to avoid resetting states, especially after client switch.
        // - Some pages require refresh to prevent state issues (due to cache).
        // - Client switch normally pushes router to dashboard; here, we stay in Claim Analysis to refetch states.
        // - Client Switch is detected and done via Switch  Client Component and once it is done we remove the preferredClient param from the query
        return totalParams !== 2;
    };
    useEffect(() => {
        getTinyContracts();
        getClaimStatuses();
        getPayersTiny();
        getNpiRoles();
        getFacilities();
        getUsersList()
        getPayerContractsTiny()
        getCodeServicesTiny()
        getDischargeStatusCodes()
    },
    [getTinyContracts, getClaimStatuses, getPayersTiny, getNpiRoles, getFacilities,getUsersList,getPayerContractsTiny,getCodeServicesTiny,getDischargeStatusCodes]);

    useEffect(() => () => {
        claimAnalysisSetDrilldown({
            open: false,
        });
    }, []);  

    // ==========
    // Page state
    const [ tableFilter, tableFilterPtr ] = useTableFilter( 'claimAnalysis-landing', isSummary ),
        { filters, isFirstTime } = tableFilterPtr.value,
        filtersPtr = tableFilterPtr.at('filters'),
        claimPageRecord = tableFilter.pagination;

    const showLowConfidenceClaims = filtersPtr?.value?.showLowConfidenceClaims;
    // Persist filters to localStorage whenever they change
    // This ensures filters are available even before the first API call
    useEffect(() => {
        if (!isFirstTime && user) {
            const currentRequest = buildClaimAnalysisRequest({
                tableFilter,
                filters,
                user,
                aiModeEnabled
            });
            
            // Persist just the filter portion for AI mode
            toLocalStorage('claimAnalysis-ai-mode-filters', currentRequest.filter);
        }
    }, [tableFilter.filters, filters.assigned, filters.significance, filters.searchTerm, user, isFirstTime]);      
        // Track previous AI mode state
    const prevAiModeEnabled = React.useRef(aiModeEnabled);
    
    // Separate useEffect ONLY for body class - must not be mixed with other deps
    // The cleanup of the other useEffect was removing the class on every filter change
    useEffect(() => {
        if (aiModeEnabled) {
            document.body.classList.add('ai-mode-enabled-claim-analysis');
        } else {
            document.body.classList.remove('ai-mode-enabled-claim-analysis');
        }
        return () => {
            document.body.classList.remove('ai-mode-enabled-claim-analysis');
            document.body.classList.remove('ai-mode-full-screen');
        };
    }, [aiModeEnabled]);

    useEffect(() => {
        const wasAiModeEnabled = prevAiModeEnabled.current;
        
        // AI Mode is being ENABLED
        if(aiModeEnabled && !wasAiModeEnabled){
            // Try to get persisted filters from localStorage first
            // This ensures filters are available even if user hasn't made changes
            let filterToUse = fromLocalStorage('claimAnalysis-ai-mode-filters');
            
            // If no persisted filters, build from current state
            if (!filterToUse) {
                const currentRequest = buildClaimAnalysisRequest({
                    tableFilter,
                    filters,
                    user,
                    skipAiFilters: true,  // Skip AI filters to get actual table filters
                    aiModeEnabled: false  // Not using AI filters here, getting base filters
                });
                filterToUse = currentRequest.filter;
            }
            
            // Store the filter snapshot (frozen until reset or AI mode disabled)
            setPreAiModeFilters(filterToUse);
            
            // Trigger API call to convert filters to natural language using useData
            if (filterToUse) {
                filtersToConvertCounterRef.current += 1;
                setFiltersToConvert({ 
                    filter: filterToUse, 
                    sort: tableFilter.sort || [],
                    counter: filtersToConvertCounterRef.current,
                    significant_variance: filters?.significance || false,
                    confidence_score: isSuperAdmin ? showLowConfidenceClaims : false
                });
            } else {
                setAiFiltersNL('');
            }
        }
        // AI Mode is being DISABLED
        else if(!aiModeEnabled && wasAiModeEnabled){
            setIsChatExpanded(false);
            
            // Persist AI-applied filters to localStorage so they survive page refresh
            if (aiAppliedFilters && aiAppliedFilters.filters) {
                toLocalStorage('claimAnalysis-ai-mode-filters', aiAppliedFilters.filters);
                toLocalStorage('claimAnalysis-filter-page', {
                    pagination: tableFilter.pagination,
                    sort: aiAppliedFilters.sort || [],
                    filter: aiAppliedFilters.filters,
                    significance: filters?.significance
                });
            }
            
            // Clear AI-specific UI states but KEEP aiAppliedFilters
            // This ensures the AI-applied filters remain active after AI mode is disabled
            setPreAiModeFilters(null);
            setAiFiltersNL("");
        }

        // Update previous state reference
        prevAiModeEnabled.current = aiModeEnabled;

    }, [aiModeEnabled, filters, tableFilter, user]); 


    // Callback to toggle chat expansion
    const handleChatExpandToggle = useCallback(() => {
        setIsChatExpanded(prev => {
            if(!prev) {
                document.body.classList.add('ai-mode-full-screen')
            } else {
                document.body.classList.remove('ai-mode-full-screen')
            }
            return !prev
        });
    }, []);

    // Generate dynamic placeholder based on current filters
    // Using more specific dependencies to prevent unnecessary recalculations
    // Simplified placeholder - use AI generated filters_nl when available
    const aiFilterPlaceholder = useMemo(() => {
        if(aiFiltersNL.includes("Applied filters")){
            return aiFiltersNL;
        }
        else if (aiFiltersNL) {
            return `AI applied filters: ${aiFiltersNL}`;
        }
        return "Your AI-applied filters will appear here.";
    }, [aiFiltersNL]);

    // Simple callbacks for AIInputField component
    const handleAiInputValueChange = useCallback((newValue: string) => {
        // Field is read-only, no changes allowed
    }, []);

    const getBindedFilters = (filters: any, customFiltersList: any[]) => {
        const advanceFilters: any[] = [];
        const quickFilters: any[] = [];
        
        // If filters is empty or null, return empty arrays
        if (!filters || !filters.operands) {
            return { advanceFilters, quickFilters };
        }

        // Flatten the AI filter structure to get all individual filters
        const flattenFilters = (filterObj: any): any[] => {
            if (!filterObj) return [];
            
            const result: any[] = [];
            
            if (filterObj.operands && Array.isArray(filterObj.operands)) {
                filterObj.operands.forEach((operand: any) => {
                    if (operand.filters && Array.isArray(operand.filters)) {
                        // This operand has filters array
                        result.push(...operand.filters);
                    } else if (operand.operands) {
                        // This operand has nested operands, recurse
                        result.push(...flattenFilters(operand));
                    }
                });
            }
            
            return result;
        };

        const allFilters = flattenFilters(filters);

        // Separate filters into custom (primary) and advanced
        allFilters.forEach((filter: any) => {
            if (!filter || !filter.id) return;

            // Check if this filter is in the custom filters list
            if (customFiltersList.includes(filter.id)) {
                // This is a primary/custom filter
                quickFilters.push({
                    field: filter.id,
                    equator: filter.equator,
                    value: filter.value
                });
            } else {
                // This is an advanced filter
                // Special handling for assignedTo filter
                if (filter.id === 'assignedTo') {
                    // Build directly instead of using getAssignedFilter() which hardcodes equator: 'equals'
                    // This preserves the actual equator from the AI response (e.g. 'not_equal_to')
                    advanceFilters.push({
                        id: 'assignedTo',
                        field: 'assignedTo',
                        equator: filter.equator,  // Use actual equator from AI (equals / not_equal_to)
                        value: filter.value,
                        actualFilter: true
                    });
                } else {
                    // Generic advanced filter
                    advanceFilters.push({
                        field: filter.id,
                        equator: filter.equator,
                        value: filter.value
                    });
                }
            }
        });

        return { advanceFilters, quickFilters };
    };
    // Callback for chat section to update filters after AI query
    const handleAIFiltersUpdate = useCallback((filtersNL: string, filters: any, top_k?: number, sort?: any[], significant_variance?: boolean, confidence_score?: boolean) => {
        
        // 1. Store filters_nl for placeholder
        setAiFiltersNL(`AI Applied filters: ${filtersNL || ''}`);
        // 2. Store the complete AI filter structure (will be used directly in buildClaimAnalysisRequest).
        // confidence_score is stored here so buildClaimAnalysisRequest can read it atomically
        // in the same state snapshot — avoids a double API call race.
        const newAiFilters = {
            filters: filters,  // Complete AI filter structure with operands
            sort: sort || [],
            top_k: top_k,
            significant_variance: significant_variance,
            confidence_score: isSuperAdmin ? (confidence_score ?? false) : false  // guard: never true for non-super_admin
        };
        setAiAppliedFilters(newAiFilters);

        // Fix 1: Update preAiModeFilters so the NEXT query inside AI mode always
        // uses the current query's result as the base filter (not the pre-AI snapshot).
        setPreAiModeFilters(filters);

        const quickFiltersList: any[] = [
            'acknowledged',      // Acknowledge filter
            'isPaid',           // Paid filter
            'flag',             // Follow-up filter
            'contract.contractState.name', // Contract state filter
            'remitdate',        // Remit date filter
            'servicedate',// Service date filter
            'followupAlert', // Follow up alert filter
            'isDenial'
        ];


        const {advanceFilters, quickFilters} = getBindedFilters(filters, quickFiltersList);
        
        // 3. Update table filters with AI-generated filters
        // Also update significance toggle based on AI's significant_variance response
        tableFilterPtr.update(prev => {
           return {
            ...prev,
            sort: sort || [],
            filters: {
                ...prev?.filters,
                filter: advanceFilters,      // Set advanced filters (like assignedTo)
                customFilter: quickFilters,  // Set custom/primary filters (like isPaid, acknowledged, etc.)
                significance: significant_variance !== undefined ? significant_variance : prev?.filters?.significance
            },
            pagination: {
                ...prev.pagination,
                currentPage: 1,
                recordsPerPage: top_k ? Math.min(top_k, 100) : 100
            }
        }});

        // Sync the toggle button state so the datatable UI reflects the AI's confidence_score.
        // Safe here because:
        //  - aiAppliedFilters?.confidence_score (primitive) is the primary dep that drives
        //    useData re-fetch, so this is at most one extra render — same as the manual toggle.
        //  - The guard ensures non-super_admin users are never affected.
        if (confidence_score !== undefined && isSuperAdmin) {
            filtersPtr.at('showLowConfidenceClaims').set(confidence_score);
        }
    }, [tableFilterPtr, isSuperAdmin]);

    const defaultFilters = useDefaultFiltersState();
    
    // Track when reset button is clicked to update preAiModeFilters after state updates
    const resetClickedRef = React.useRef(false);

    // Callback to clear all filters and reset to default criteria
    const handleClearAllFilters = useCallback(() => {
        // Clear AI filters natural language text
        // setAiFiltersNL('');
        
        // Clear AI applied filters
        setAiAppliedFilters(null);
        
        // Reset to default filters
        const defaults = defaultFilters();
        filtersPtr.set(defaults);
        
        // Mark that reset was clicked so useEffect can update preAiModeFilters
        if (aiModeEnabled) {
            resetClickedRef.current = true;
        }
    }, [filtersPtr, defaultFilters, aiModeEnabled]);
    
    // Update preAiModeFilters after reset button is clicked and state updates
    useEffect(() => {
        if (resetClickedRef.current && aiModeEnabled && user) {
            // Build the filter structure with the actual current state (after reset)
            const currentRequest = buildClaimAnalysisRequest({
                tableFilter,
                filters,
                user,
                aiModeEnabled
            });
            
            // Update preAiModeFilters with the actual default filter state
            setPreAiModeFilters(currentRequest.filter);
            
            // Also update localStorage
            toLocalStorage('claimAnalysis-ai-mode-filters', currentRequest.filter);
            
            // Trigger API call to convert filters to natural language using useData
            // Force a new object reference to trigger useData even if filter content is the same
            if (currentRequest.filter) {
                filtersToConvertCounterRef.current += 1;
                setFiltersToConvert({ 
                    filter: currentRequest.filter, 
                    sort: tableFilter.sort || [],
                    counter: filtersToConvertCounterRef.current, // Increment counter to force new reference
                    significant_variance: filters?.significance || false,
                    confidence_score: isSuperAdmin ? showLowConfidenceClaims : false
                });
            } else {
                setAiFiltersNL('');
            }
            
            // Reset the flag
            resetClickedRef.current = false;
        }
    }, [aiModeEnabled, tableFilter.filters, filters, user, resetClickedRef.current]);

    const userSession = useGlobalStore( x => x.session.user.id );

     const isAssignedFilterApplied = () => {
        const appliedAssignedFilters = filtersPtr?.value?.filter?.filter(
            (item) => item?.field === "assignedTo"
        );

        return appliedAssignedFilters?.length > 0;
    };


    // Fetch saved search criteria
    const { data : searchCriteriaList, isPending : isPendingSearchCriteria, reload: reloadCriteriaList } = useData( async () => {
        if( !isSummary && notIsSwitchingClientAsPerUserRequest()) {
            const [ dataForPage, defaultCriteria ] = await fetchSearchCriteriaForPage( 'claimAnalysis-landing', userSession );
            
            // First time, initialise the filter with the default filter
            if( isFirstTime 
                // If selected filter was deleted...
                || ( filters.selectedFilterId && !dataForPage.find( x => x.id === filters.selectedFilterId ) ) 
                // If default filter is applied and default filter was changed...
                || ( filters.defaultFilterId && filters.selectedFilterId === filters.defaultFilterId && filters.defaultFilterId !== defaultCriteria?.id)
            ) {
                tableFilterPtr.update( prev => ({
                    ...prev,
                    isFirstTime: false,

                    ...( defaultCriteria ? {
                        filters : {
                            ...prev.filters,
                            ...defaultCriteria.filters,
                            significance:defaultCriteria?.filters?.significance || false,
                            selectedFilterId: defaultCriteria.id,
                            defaultFilterId: defaultCriteria.id
                        }
                    } : {
                        filters : {
                            ...defaultFilters(),
                            filter:prev?.filters?.filter || [],
                            selectedFilterId: null,
                            defaultFilterId: null,
                        }
                    } )
                    
                }));
            }

            return dataForPage;
        }
    }
    , [totalParams]);

    const selectedCriteria = useMemo( () =>
        searchCriteriaList?.find( x => x.id === filters.selectedFilterId )
    , [ searchCriteriaList, filters.selectedFilterId ] );


    const {
    data,
    isPending: isClaimsPending,
    error: claimsError,
    reload: reloadClaims
} = useData(async abort => {

    try {
    if( !isPendingSearchCriteria && !isFirstTime &&  notIsSwitchingClientAsPerUserRequest()) 
    {
    const ignoreCall = isClaimActionAppliedEnabled(filters);
    if (ignoreCall && !aiModeEnabled) {
        return;
    }
    const request = buildClaimAnalysisRequest({
        tableFilter,
        filters,
        user,
        aiModeEnabled
    });

    const list = await fetchClaimAnalysisGridData(
        request,
        abort
    );

    toLocalStorage(
        'claimAnalysis-backup',
        list.results.map(item => item.id)
    );

    toLocalStorage(
        'claimAnalysis-backup-page',
        list.results.map(item => ({
            id: item.id,
            page: request.pagination.currentPage
        }))
    );

    tableFilterPtr.update(prev => ({
        ...prev,
        filters: {
            ...prev.filters,
            showIgnoredFilterDialog:
                !prev.filters.ignoreAcknowledgement &&
                list.results?.length === 0 &&
                prev.filters.searchTerm?.length > 2
        },
        pagination: list.page
    }));

    return list;
    }
    }
    catch (error) {
        if (error?.name === 'AbortError' || error?.message?.includes("concurrent operation")) return // Avoid Toaster in abort case
        generateErrorToaster(error)
    }
    return [];
}, [
    isPendingSearchCriteria,
    isFirstTime,
    tableFilter.filters,
    tableFilter.sort,
    tableFilter.pagination.currentPage,
    tableFilter.pagination.recordsPerPage,
    filters?.searchTerm,
    filters?.significance,
    filters?.assigned,
    totalParams,
    showLowConfidenceClaims,
    aiAppliedFilters?.confidence_score  // re-fetch when AI changes low-confidence endpoint
]);

    // Fix 2: Show toaster error for analyses API only when AI mode is enabled
    useEffect(() => {
        if (!claimsError || !aiModeEnabled) return;
        const errMsg =
            claimsError?.payload?.error ||
            claimsError?.payload?.message ||
            claimsError?.message ||
            'An error occurred while loading claims. Please try again.';
        errorToastify(
            <ToastMessage
                errorMessage={errMsg}
                innerException={claimsError?.payload?.innerException}
                errorDetails={claimsError?.payload?.errorDetails}
            />
        );
    }, [claimsError, aiModeEnabled]);

const {
    isPending: isTotalsPending,
    reload: reloadTotals
} = useData(async abort => {
if( !isPendingSearchCriteria && !isFirstTime &&  notIsSwitchingClientAsPerUserRequest()) 
{
    const ignoreCall = isClaimActionAppliedEnabled(filters);
    if (ignoreCall && !aiModeEnabled) {
        return;
    }
    const request = buildClaimAnalysisRequest({
        tableFilter,
        filters,
        user,
        aiModeEnabled
    });

    const response = await fetchClaimAnalysisTotals(
        request,
        abort
    );
    setTotals(response);

    return response;
}
return {};
}, [
    isPendingSearchCriteria,
    isFirstTime,
    tableFilter.filters,
    tableFilter.sort,
    tableFilter.pagination.currentPage,
    tableFilter.pagination.recordsPerPage,
    filters?.searchTerm,
    filters?.significance,
    filters?.assigned,
    totalParams,
    showLowConfidenceClaims,
    aiAppliedFilters?.confidence_score  // re-fetch totals when AI changes low-confidence endpoint
]);



    function buildClaimAnalysisRequest({
    tableFilter,
    filters,
    user,
    skipAiFilters = false,
    aiModeEnabled = false  // NEW: Only use AI filters when AI mode is actively enabled
}) {
    // Only use AI filters if AI mode is currently enabled
    // This allows users to change filters normally after AI mode is disabled
if (aiAppliedFilters && !skipAiFilters && aiModeEnabled) {
    let aiFilter = aiAppliedFilters.filters;

    const hasClaimActionApplied = aiFilter?.operands?.some(operand =>
        operand.filters?.some(f => 
            f.id === 'isClaimActionApplied' && 
            (f.value === true || f.value === 'true')
        )
    );

    if (hasClaimActionApplied) {
        // Remove existing isPaid and isDenial operands entirely (regardless of their value)
        const filteredOperands = (aiFilter?.operands || []).filter(operand =>
            !operand.filters?.some(f => f.id === 'isPaid' || f.id === 'isDenial')
        );

        aiFilter = {
            ...aiFilter,
            operands: [
                ...filteredOperands,
                { operator: 'and', filters: [{ id: 'isPaid', equator: 'equals', value: true }] },
                { operator: 'and', filters: [{ id: 'isDenial', equator: 'equals', value: true }] },
            ]
        };
    }

    return {
        pagination: tableFilter.pagination,
        sort: aiAppliedFilters.sort,
        filter: aiFilter,
        significance: filters?.searchTerm?.length > 0 ? false : filters?.significance,
        // Read confidence_score atomically from aiAppliedFilters — this is always in sync
        // with the current AI response and avoids the double-render race caused by
        // reading showLowConfidenceClaims (a separate state update that lands one render later).
        showLowConfidenceClaims: isSuperAdmin && (
            (aiAppliedFilters.confidence_score ?? showLowConfidenceClaims) ||
            (filters?.searchTerm?.length > 2)
        )
    };
}
    
    // Normal filter processing (when AI is not active)
    const { sort, filters: filter, pagination } = tableFilter;

    traceChanges([ isPendingSearchCriteria, isFirstTime, tableFilter.filters, tableFilter.sort, tableFilter.pagination.currentPage, tableFilter.pagination.recordsPerPage, filters?.searchTerm, filters?.significance ]);

    const assignedFilter = isAssignedFilterApplied()
        ? null
        : getAssignedFilter(user?.username);

    const newOperands = filter?.operands;
    
    // @ts-ignore
    let commaEscapedFilters={ ...filter, operands : (filters?.assigned && assignedFilter)  ? [...(newOperands || []),assignedFilter]:newOperands } || null;

    const finalFilter = !filter ? filters?.assigned ? commaEscapedFilters:  null : commaEscapedFilters;

    toLocalStorage('claimAnalysis-filter-page',{
                 pagination, sort,
                 filter: finalFilter,
                 significance: filters?.searchTerm?.length > 0 ? false : filters?.significance,
             }); 

    // Also persist just the filter portion for AI mode
    // This ensures filters are available when AI mode is enabled, even without user changes
    toLocalStorage('claimAnalysis-ai-mode-filters', finalFilter);

    return {
        pagination,
        sort,
        filter: finalFilter,
        significance:
            filters?.searchTerm?.length > 0
                ? false
                : filters?.significance,
        showLowConfidenceClaims: isSuperAdmin && (showLowConfidenceClaims || (filters?.searchTerm?.length > 2))
    };
}

    const handleRefresh = () =>{
        reloadClaims();
        reloadTotals();
    }


     const {
            data:uploadResponse,
            isPending:isPendingUpload,
            error:importError,
        } = useData(
            async () => {
                if (!importFile) return;
             const uploadResponse =  await importRcmAssignmentFile(importFile)
             handleRefresh()
             return uploadResponse
            },
            [importFile]
        );

        useEffect(() => {
            if (!importError) return;
            errorToastify(
                <ToastMessage
                    errorMessage={importError?.payload?.error}
                    innerException={importError?.payload?.innerException}
                    errorDetails={importError?.payload?.errorDetails}
                />
            );
        }, [importError]);



    const claimsData = data?.results || [],
        pending = Boolean( isClaimsPending || isPendingSearchCriteria );

    const handleAcknowledge = ({ id }) => {
        setBulkAcknowledge(false);
        selectedClaimsIdsPtr.set([id]);
        dialogsOpenPtr.at('acknowledgeDialog').set(true);
    }

    const handleAcknowledgeClose = (shouldReloadGrid) => {
        dialogsOpenPtr.at('acknowledgeDialog').set(false);
        dialogsOpenPtr.at('flagDialog').set(false);
        setFlagToggle(false);

        if (shouldReloadGrid) {
            handleRefresh();
        }
    }

    function handleBulkAcknowledge() {
        dialogsOpenPtr.at('acknowledgeDialog').set(true);
        setBulkAcknowledge(true);
    }

    function handleDowloadTemplate(onlyVisibleColumns=false,hasEapgCodeAccess) {
        let operandsWithoutBoth = tableFilter.filters?.operands.filter(operand =>
            !operand.filters.some(filter => filter.value === 'both')
        );
        let filterWithoutBoth = {
            ...tableFilter.filters,
            operands: operandsWithoutBoth || [],
        };
        const allGridColumns=allClaimAnalysisGridColumns(hasEapgCodeAccess, isSuperAdmin, hasPatternIntelligenceAccess)
       const hiddenColumns =fromLocalStorage("claimAnalysis-landing-visibility")  || {}
        const uiVisibleColumns = allGridColumns?.filter(column => {
                const key = column.enum || column.enumInGrid;
                if (!key) return true; 

                return hiddenColumns[key] !== false && !column?.hideFromGrid;
        });

        const payload = {
            page: null,
            filter: filterWithoutBoth || null,
            sort: tableFilter.sort || [],
            columns:onlyVisibleColumns ?uiVisibleColumns: allGridColumns
        };
        downloadAnalysisReport(payload, filters.significance, downloadTemplateCallback, isSuperAdmin && showLowConfidenceClaims);
    }

    function downloadTemplateCallback(blob) {
        infoToastify(
            <>
                <b>Claim Analysis Export</b>
                <br />
                Your export request is being processed. You will be notified once the file is ready.
            </>,
        );
    }

    const handleProcessClaimCallback = () => {
        setOpenPlayDialog(false);
    }

    const handleSingleFlag = (flag, { id }) => {
        // add optional date if flag is true
        if (flag) {
            dialogsOpenPtr.at('flagDialog').set(true);
            setFlagToggle(flag); // pre set in Flag Dialog
            setBulkFlagged(false);
            selectedClaimsIdsPtr.set([id]);
        } else {
            // directly call api on flag is false
            let payload = {
                analysisIds: [id],
                flag: flag,
                followupAlert: ''
            }
            flagClaims(payload, handleAcknowledgeClose, true);
        }
    }

    const handleBulkFlag = () => {
        dialogsOpenPtr.at('flagDialog').set(true);
        setBulkFlagged(true);
    }

    function handleFilterClear() {
        filtersPtr.set(defaultFilters());
    }

  function resetAllFilterSettingsToDefault() {
        const assignedFilter = getAssignedFilter(preferredAssignee, true);

        tableFilterPtr.update((prev) => ({
            ...prev,
            isFirstTime: false,

            filters: {
                ...prev?.filters,
                filter: [assignedFilter],
                customFilter: [],
                significance:false
            },
        }));
    }

    const selectedClaimsIdsPtr = useStatePtr<number[]>([]);

    const dialogsOpenPtr = useStatePtr(()=>({
        acknowledgeDialog: false,
        flagDialog: false,
        processClaimsDialog: false,
        updateStatusDialog: false,
        assignClaimsDialog:false,
        assignRcmTeamsDialog:false,
        copyClaimDialog:false
    }));

    useEffect(() => {
        if (totalParams===2 && preferredAssignee) {
            resetAllFilterSettingsToDefault()
        }
    }, [totalParams]);



    return (
        <div className={'main-content-area-claim'}>
            <Helmet title="Claim Analysis" />
            <Grid container sx={{ position: "relative" }}>
                <Grid item xs={12}>
                    <BreadCrumb routes={routes} isFromClaims={true} />
                </Grid>

                <Box
                    sx={{
                        position: "absolute",
                        right: "10px",
                        display:"flex",
                        columnGap:"30px",
                    }}
                >
                    <ClaimAnalysisFollowUpFilter
                        handleRefresh={handleRefresh}
                        filtersPtr={filtersPtr}
                        pending={pending}
                        customFilter={filters.customFilter}
                        disabled={aiModeEnabled} 
                    />
                    <SelectSearchTitle
                        pending={isPendingSearchCriteria || pending}
                        searchFilters={searchCriteriaList}
                        selectedPtr={
                            filtersPtr
                                .pipe( next => ({
                                    // Assign selected search criteria to filters when selectedFilterId changes
                                    ...next,
                                    ...searchCriteriaList.find( x => x.id === next.selectedFilterId ).filters
                                }))
                                .at('selectedFilterId')
                        }
                    disabled={aiModeEnabled} 
                    />
                    {hasClaimSemanticSearchAccess && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={aiModeEnabled}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        setAiModeEnabled(newValue);
                                    }}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label="AI Mode"
                            sx={{
                                minWidth: "100px",
                                flexDirection: 'column-reverse',
                                margin: 0,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '11px',
                                    color: aiModeEnabled ? '#667eea' : 'inherit'
                                }
                            }}
                        />
                    )}
                </Box>

            </Grid>


            {/* Main content wrapper with AI Chat sidebar */}
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                width: '100%', 
                gap: 0,
            }}>
                {/* Main content area - takes remaining space */}
                <Box sx={{ 
                    flex: isChatExpanded ? 0 : 1,
                    minWidth: 0,
                    opacity: isChatExpanded ? 0 : 1,
                    width: isChatExpanded ? 0 : 'auto',
                    transition: 'all 0.3s ease-in-out',
                }}>
            <Portlet>
                <Box className={classes.statisticsContainer}>
                    <ClaimStatistics
                        data={totals}
                        pending={isTotalsPending}
                    />
                </Box>

                <Box sx={aiModeEnabled ? { 
                            '& .MuiIconButton-root[color="error"], & .filter-clear-btn, & [class*="deleteIcon"], & button:has(svg[data-testid="DeleteIcon"])': { 
                                display: 'none !important' 
                            },
                            '& .kt-portlet__head-toolbar': {
                                flex: 1,
                                width: '100%',
                            }
                        } : {}}>
                <PortletHeader
                    pending={isClaimsPending!=null}
                    title="Claim Analysis"
                    name="claimAnalysis-landing"
                    handleRefresh={handleRefresh}
                    showSearchFilters={!aiModeEnabled}
                    filter={aiModeEnabled ? [] : filters.filter}
                    handleFilterClear={aiModeEnabled ? undefined : handleFilterClear}
                    includeAdvanceFilters={!aiModeEnabled}
                    toolbar={
                         <PortletHeaderToolbar style={aiModeEnabled ? { width: '100%', flex: 1 } : {}}>
                                    {aiModeEnabled ? (
                                        <Stack direction="column" sx={{ width: '100%' }}>
                                            {/* Read-only text display for applied filters */}
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: '8px',
                                                    border: '1px solid #e0e0e0',
                                                    minHeight: '48px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        color: aiFiltersNL ? '#333' : '#999',
                                                        fontStyle: aiFiltersNL ? 'normal' : 'italic',
                                                    }}
                                                >
                                                    {aiFilterPlaceholder}
                                                </Typography>
                                            </Box>
                                            {/* Error message when no AI access */}
                                            {!haveAIAccess && !isFetchingCredits && (
                                                <Typography sx={{ color: 'red', fontSize: '12px', mt: 0.5, ml: 1 }}>
                                                    <b>Note:</b>{" "}
                                                    {!hasClaimSemanticSearchAccess
                                                        ? "AI Features are disabled for this client. Contact admin."
                                                        : "You don't have enough credits to use this feature. Contact the admin to upgrade."}
                                                </Typography>
                                            )}
                                        </Stack>
                                    ) : (
                                        <ClaimAnalysisTopFilters pendingState={ pending } handleRefresh={handleRefresh} relativeFilter={relativeFilter} tableFiltersPtr={ filtersPtr }/>
                                    )}
                        </PortletHeaderToolbar>
                    }
                />
                </Box>

                <PortletBody>
                    <Stack direction="column" spacing={1} sx={{ height: 'calc(100vh - 420px)', '@media (max-width: 1550px)': { height: 'calc(100vh - 493px)' } }}>
                        <TableLevelAction
                            handleRefresh={handleRefresh}
                            actions={ () =>
                                <>
                                    {isSuperAdmin && (
                                        <Grid item>
                                            <Tooltip title={showLowConfidenceClaims ? "Hide Low Confidence Claims" : "Show Low Confidence Claims"}>
                                                <IconButton aria-label="toggle-confidence" size='small' onClick={() => {
                                                    filtersPtr.set({...filtersPtr?.value,showLowConfidenceClaims:!filtersPtr?.value?.showLowConfidenceClaims})
                                                }
                                                }>
                                                    {filtersPtr?.value?.showLowConfidenceClaims ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    )}
                                {selectedClaimsIdsPtr.value.length > 0 && (
                                    <>
                                        {(isUniversalAdmin && (selectedClientName !== (isDevEnv ? "SlicedPreviewOne" : "Sliced Preview"))) && (
                                            <Grid item>
                                                <Tooltip title="Copy Claim(s) to Preview client">
                                                    <IconButton aria-label="copyclaim" size='small' onClick={ () => dialogsOpenPtr.at('copyClaimDialog').set(true) } disabled={claimAnalysisDownloadReportPending}>
                                                        <CopyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        )}

                                        <Grid item>
                                            <Tooltip title="Update Status">
                                                <IconButton aria-label="process" size='small' onClick={() => dialogsOpenPtr.at('updateStatusDialog').set(true) } disabled={claimAnalysisDownloadReportPending}>
                                                    <UpdateOutlined />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>

                                        {(canAccessRCM) && 
                                        <Grid item>
                                            <Tooltip title="Assign RCM Team">
                                                <IconButton aria-label="process" size='small' onClick={() => dialogsOpenPtr.at('assignRcmTeamsDialog').set(true) }>
                                                    <GroupsIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>}

                                        {hasClaimProcessorAccess && (
                                            <Grid item>
                                                <Tooltip title="Process Claims">
                                                    <IconButton aria-label="process" size='small' 
                                                        onClick={ () => dialogsOpenPtr.at('processClaimsDialog').set(true) } 
                                                        disabled={claimAnalysisDownloadReportPending}
                                                    >
                                                        <PlayCircleOutlineIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        )}


                                            <Grid item>
                                                <Tooltip title="Assign Claims">
                                                    <IconButton aria-label="process" size='small' 
                                                        onClick={() => dialogsOpenPtr.at('assignClaimsDialog').set(true) } 
                                                        disabled={claimAnalysisDownloadReportPending}
                                                    >
                                                        <PersonAddAltIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        </>
                                    )}

                                    {/* Import Button */}
                                    {canAccessRCM && <Grid item>
                                        <Tooltip title="Import RCM Teams">
                                            <IconButton
                                                aria-label="import"
                                                size="small"
                                                onClick={handleImportFileInput}
                                                disabled={claimAnalysisDownloadReportPending}
                                            >
                                                <UploadIcon />
                                                <input
                                                    type="file"
                                                    hidden
                                                    ref={importInputRef}
                                                    onChange={handleFileChange}
                                                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>}

                                    <ExportPopover
                                        handleDowloadTemplate={
                                            handleDowloadTemplate
                                        }
                                    />

                                    {selectedClaimsIdsPtr.value.length > 0 && (
                                        <>

                                        <Grid item>
                                            <Tooltip title="Follow">
                                                <IconButton aria-label="follow" size='small' onClick={handleBulkFlag}>
                                                    <FlagIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>

                                        <Grid item>
                                            <Tooltip title="Acknowledge">
                                                <IconButton aria-label="acknowledge" size='small' onClick={handleBulkAcknowledge}>
                                                    <CheckCircleIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    </>
                                )}
                                </>
                            }
                            
                            loading={claimAnalysisDownloadReportPending || isPendingUpload!==null}
                            actionLevelGrid={ () =>
                                <Grid container>
                                    <Grid item md={12}>
                                    <h6 className="">{`Total Records : ${(claimsData?.length > 0 && claimPageRecord?.totalRecords) ? claimPageRecord?.totalRecords : 0}`}</h6>
                                    </Grid>
                                </Grid>
                            }
                        />

                        <ClaimAnalysisDataGrid
                        rcmAssignmentData={rcmAssignmentData}
                            defaultCustomFilter={defaultCustomFilters}
                            selectedClaimsIdsPtr={selectedClaimsIdsPtr}
                            data={claimsData}
                            handleAcknowledge={handleAcknowledge}
                            handleSingleFlag={handleSingleFlag}
                            loading={ Boolean(pending) || !notIsSwitchingClientAsPerUserRequest() || isPendingUpload!==null}
                            filtersPtr={ filtersPtr }

                            paginationFilters={tableFilter}
                            paginationPtr={tableFilterPtr.at('pagination')}
                            sortPtr={tableFilterPtr.at('sort')}
                            refreshCriteriaList={ setDefault => {
                                setDefault && tableFilterPtr.at('isFirstTime').set(true);
                                reloadCriteriaList();
                            }}
                            showSearchFilters={true}
                            selectedCriteria={ selectedCriteria }
                            filteredUsers={userData}
                        />
                    </Stack>

                </PortletBody>
            </Portlet>
            </Box>

                {/* AI Chat Section - fixed width sidebar or full width when expanded */}
                {aiModeEnabled && (
                    <AIChatSection 
                        open={aiModeEnabled}
                        onClose={() => {
                            setAiModeEnabled(false);
                            setIsChatExpanded(false);
                        }}
                        selectedClaimIds={selectedClaimsIdsPtr?.value || []}
                        totalClaimsCount={claimPageRecord?.totalRecords || 0}
                        externalMessage={aiMessageToSend}
                        onMessageSent={() => {
                            setAiCustomFilter('');
                            setAiMessageToSend('');
                        }}
                        onFiltersUpdate={handleAIFiltersUpdate}
                        isExpanded={isChatExpanded}
                        onExpandToggle={handleChatExpandToggle}
                        onClearAllFilters={handleClearAllFilters}
                        preAiModeFilters={preAiModeFilters}
                        significanceFlag={filters?.significance || false}
                        confidenceScoreFlag={isSuperAdmin ? showLowConfidenceClaims : false}
                    />
                )}
            </Box>

            {/* Dialogs - placed outside the flex container */}
            <AcknowledgeDialog
                open={dialogsOpenPtr.value.acknowledgeDialog}
                handleClose={handleAcknowledgeClose}
                selectedItemsPtr={selectedClaimsIdsPtr}
            />

            <FlagDialog
                handleClose={handleAcknowledgeClose}
                openPtr={dialogsOpenPtr.at('flagDialog')}
                selectedItemsPtr={selectedClaimsIdsPtr}
            />

            <ProcessClaimDialog
                openPtr={dialogsOpenPtr.at('processClaimsDialog')
                    .onChange( open => {
                        if (!open) {
                            resetClaimProcessError();                        
                        }
                    })
                }
                
                selectedItemsPtr={selectedClaimsIdsPtr}

                handleProcessClaimCallback={handleProcessClaimCallback}
                claimsData={claimsData}
                filter={tableFilter.filters}
                tableFiltersPtr={ filtersPtr }
                showLowConfidenceClaims={showLowConfidenceClaims}
            />

            <UpdateStatusDialog 
                openPtr={dialogsOpenPtr.at('updateStatusDialog')}
                selectedItemsIdsPtr={selectedClaimsIdsPtr}
                claimStatusList={claimStatusList?.map((item)=>({label:item.name,value:item.id,name:item.name}))} 
                handleRefresh={handleRefresh}
            />

            <ClaimAssignmentDialog 
                openPtr={dialogsOpenPtr.at('assignClaimsDialog')}
                handleClose={()=>  dialogsOpenPtr.at('assignClaimsDialog').set(false)}
                selectedClaimsIds={selectedClaimsIdsPtr?.value}
                callBack={handleRefresh}
                dataUsers={userData}
                needRefetch={false}
            
            />

            <CopyClaimDialog
                openPtr={dialogsOpenPtr.at('copyClaimDialog')}
                selectedItemsPtr={selectedClaimsIdsPtr}
                claimsData={claimsData}
                filter={tableFilter.filters}
                tableFiltersPtr={ filtersPtr }
            />

          {canAccessRCM &&  <RCMTeamAssignmentDialog selectedClaimsIds={selectedClaimsIdsPtr?.value} openPtr={dialogsOpenPtr.at('assignRcmTeamsDialog')} handleClose={()=>  dialogsOpenPtr.at('assignRcmTeamsDialog').set(false)}
            callBack={handleRefresh} rcmAssignmentData={rcmAssignmentData}
/>}

            <IgnoreFiltersDialog
                filtersPtr={
                    filtersPtr.onChange( obj => {
                        // Removed the code inside it because it is redundant , We are alreading calling the api on filtrs change , Then again calling handleRefresh here causes abort issues 
                    })
                }
            />
        </div>
    );
}